import { DatePicker, TimePicker } from "antd/es";
import Tag from "antd/es/tag";
import { Guid } from "guid-typescript";
import _ from "lodash";
import moment from "moment";
import { PickerSharedProps } from "rc-picker/es/Picker";
import { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import isEqual from "react-fast-compare";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import {
  clearPanelHighlight,
  confirmOpenPanel,
  getPanelStopCount,
  highlightPanelStop,
  navigatePanelStop,
  PanelArrowKey
} from "../../../shared/utilty/datetime-panel-keyboard";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";
import { ReactSystemFunctions } from "../../../shared/utilty/react-system-functions";
import { CommonProps } from "../common-props";
import { DashboardState } from "../kuika-cl-model-runtimes";

declare let window: any;

const ARROW_KEYS: Record<number, PanelArrowKey> = { 37: "left", 38: "up", 39: "right", 40: "down" };

enum Displays {
  Date = 1,
  Time = 2,
  DateTime = 3,
  Month = 4
}
interface DateProps extends PickerSharedProps<any> {
  value?: string | moment.Moment | Date | (string | moment.Moment | Date)[];
  defaultValue?: string | moment.Moment | Date;
  format?: string;
  displayFormat?: string;
  use12Hours?: boolean;
  hourStep?: number;
  minuteStep?: number;
  secondStep?: number;
  onChange?: (value: string | null) => void;
  returnDisplayValue?: boolean;
  style?: any;
  placeholder?: string;
  disableBeforeDate?: any;
  disableAfterDate?: any;
  disableBeforeTime?: any;
  disableAfterTime?: any;
  className?: string;
  disabledDays?: (
    | "Monday"
    | "Tuesday"
    | "Wednesday"
    | "Thursday"
    | "Friday"
    | "Saturday"
    | "Sunday"
    | "Weekends"
    | "Weekdays"
  )[];
  displayType?: "datetime" | "date" | "time" | "month";
  isTableSearch?: boolean;
  multiple?: boolean;
}

interface DateState {
  uniqueKey: Guid;
  isOpen: boolean;
  disableBeforeDate?: any;
  disableAfterDate?: any;
  disableBeforeTime?: any;
  disableAfterTime?: any;
}

interface AntdLocaleShape {
  locale?: string;
}

const localesForcedToMonday: Set<string> = new Set<string>();

/**
 * Force a moment locale to start its week on Monday (KOA-17435).
 *
 * The antd DatePicker is moment-backed (via @ant-design/moment-webpack-plugin) and has no
 * `firstDayOfWeek` prop — it derives the panel's first weekday from
 * `moment().locale(name).localeData().firstDayOfWeek()`. Locales such as "en" default to Sunday (0),
 * which is what regressed the picker away from the expected Monday start.
 *
 * `moment.updateLocale` switches the active global locale as a side effect, so the previous one is
 * restored. Results are cached per locale name so repeated renders don't re-run the update. Week
 * config only affects week-of-year math (`week()` / `startOf("week")`), which this app does not use
 * — `isoWeekday()` is Monday-based regardless — so the change is scoped to the picker's appearance.
 */
const forceMondayFirstDayOfWeek = (localeName: string): void => {
  if (!localeName || localesForcedToMonday.has(localeName)) {
    return;
  }
  const previousLocale: string = moment.locale();
  moment.updateLocale(localeName, { week: { dow: 1, doy: 4 } });
  moment.locale(previousLocale);
  localesForcedToMonday.add(localeName);
};

class KDatePicker extends PureComponent<DateProps & CommonProps, DateState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kdatepicker_${this.uniqueKey}`);

  // Highlighted stop in the open panel: -1 = none (date grid / typing), 0..n = panel stops.
  private panelFocusIndex: number = -1;

  // Multiple mode: set right before a selection commits so the selection-driven
  // onOpenChange(false) that antd fires next is recognised and ignored (KOA-17737).
  private keepMultiplePanelOpen: boolean = false;

  constructor(props: DateProps) {
    super(props);
    this.state = {
      uniqueKey: Guid.create(),
      isOpen: false,
      disableBeforeDate: undefined,
      disableAfterDate: undefined,
      disableBeforeTime: undefined,
      disableAfterTime: undefined
    };
  }

  componentDidMount = () => {
    this.setDynamicStyle();
    if (this.props && this.props.style && this.props.style?.fontSize) {
      const node = ReactDOM.findDOMNode(this) as Element;
      const inputs = node.getElementsByTagName("input");
      if (inputs && inputs.length) {
        for (let index = 0; index < inputs.length; index++) {
          inputs[index].style.fontSize = this.props.style?.fontSize;
        }
      }
    }
  };

  componentDidUpdate(prevProps: DateProps) {
    if (!isEqual(prevProps, this.props)) {
      this.setDynamicStyle();
    }
  }

  componentWillUnmount = () => {
    this.style.removeStyle();
  };

  getCalculatedDisplay = (): Displays => {
    if (this.props.isTableSearch) {
      const displayType = this.props.displayType;
      if (displayType === "datetime") return Displays.DateTime;
      if (displayType === "date") return Displays.Date;
      if (displayType === "time") return Displays.Time;
      if (displayType === "month") return Displays.Month;

      const fmt = this.props.format || "";
      if (!fmt) return Displays.Date;
      const detectedDisplay = this.detectDisplayFromFormat(fmt);
      if (detectedDisplay) return detectedDisplay;

      const hasHour = fmt.includes("HH") || fmt.includes("H") || fmt.includes("hh") || fmt.includes("h");
      const hasMinute = fmt.includes("mm");
      const hasSecond = fmt.includes("ss");
      const hasIsoT = fmt.includes("T");
      const hasAnyTime = hasHour || hasMinute || hasSecond || hasIsoT;
      const hasDateTokens = fmt.includes("Y") || fmt.includes("D") || fmt.includes("M");

      if (this.props.isTableSearch && typeof this.props.value === "string") {
        const strVal = this.props.value as string;
        const hasTimeInValue = strVal.includes(":") || strVal.includes("T");
        if (hasTimeInValue) return Displays.DateTime;
      }

      if (hasAnyTime && hasDateTokens) return Displays.DateTime;
      if (hasAnyTime && !hasDateTokens) return Displays.Time;
      if (fmt === "YYYY-MM") return Displays.Month;
      return Displays.Date;
    }
    if (!this.props.format || this.props.format == "") {
      if (this.props.displayType === "datetime") {
        return Displays.DateTime;
      }
      if (this.props.displayType === "date") {
        return Displays.Date;
      }
      if (this.props.displayType === "time") {
        return Displays.Time;
      }
      if (this.props.displayType === "month") {
        return Displays.Month;
      }
      return Displays.Date;
    }
    const detectedDisplay = this.detectDisplayFromFormat(this.props.format);
    if (detectedDisplay) return detectedDisplay;
    let calculatedDisplay = Displays.Date;
    switch (this.props.format) {
      case "DD/MM/YYYY":
      case "DD.MM.YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD/YYYY":
      case "MM.DD.YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/MM/DD":
      case "YYYY.MM.DD":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY/DD/MM":
      case "YYYY.DD.MM":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY-MM-DD":
      case "DD-MM-YYYY":
        calculatedDisplay = Displays.Date;
        break;
      case "MM/DD":
      case "MM.DD":
        calculatedDisplay = Displays.Date;
        break;
      case "D M Y":
        calculatedDisplay = Displays.Date;
        break;
      case "YYYY-MM-DD HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm":
        calculatedDisplay = Displays.DateTime;
        break;
      case "DD-MM-YYYY HH:mm:ss":
        calculatedDisplay = Displays.DateTime;
        break;
      case "mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "hh:mm:ss":
        calculatedDisplay = Displays.Time;
        break;
      case "HH:mm":
        calculatedDisplay = Displays.Time;
        break;
      case "YYYY-MM":
        calculatedDisplay = Displays.Month;
        break;
      default:
        calculatedDisplay = Displays.Date;
        break;
    }
    return calculatedDisplay;
  };

  private detectDisplayFromFormat = (format: string): Displays | null => {
    if (!format) return null;
    const trimmedFormat = format.trim();
    if (!trimmedFormat) return null;
    const cleanedFormat = trimmedFormat.replace(/\[[^\]]*]/g, "");
    const hasHourToken = /H|h|k|K/.test(cleanedFormat);
    const hasMinuteToken = /m/.test(cleanedFormat);
    const hasSecondToken = /s|S/.test(cleanedFormat);
    const hasMeridiemToken = /a|A/.test(cleanedFormat);
    const hasIsoToken = cleanedFormat.includes("T");
    const hasAnyTimeToken = hasHourToken || hasMinuteToken || hasSecondToken || hasMeridiemToken || hasIsoToken;
    if (!hasAnyTimeToken) return null;
    const hasYearToken = /Y|y/.test(cleanedFormat);
    const hasDayToken = /D|d/.test(cleanedFormat);
    const hasMonthToken = /M/.test(cleanedFormat);
    const hasDateToken = hasYearToken || hasDayToken || (hasMonthToken && (hasYearToken || hasDayToken));
    if (hasDateToken) return Displays.DateTime;
    return Displays.Time;
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  setValue = (value: string | moment.Moment | Date) => {
    if (moment.isMoment(value)) {
      this.handleChange(value);
      return;
    }

    if (value instanceof Date) {
      this.handleChange(moment(value));
    }

    if (typeof value === "string") {
      this.handleChange(moment(value));
    }

    if (value === null || value === undefined) {
      this.handleChange(moment());
    }
  };

  getDynamicCss = (): string => {
    const className: string = this.getClassName();
    if (!className || className.length === 0) {
      return "";
    }

    let result = "";
    if (this.props.style?.color || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input {
            `;
      if (this.props.style?.color) result += `color: ${this.props.style?.color} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.fontSize || this.props.style?.fontFamily || this.props.style?.fontWeight) {
      result += `
          .${className.trim()} .ant-picker-input>input::-webkit-input-placeholder {
            `;
      if (this.props.style?.fontSize) result += `font-size: ${this.props.style?.fontSize} !important;`;
      if (this.props.style?.fontFamily) result += `font-family: ${this.props.style?.fontFamily} !important;`;
      if (this.props.style?.fontWeight) result += `font-weight: ${this.props.style?.fontWeight} !important;`;
      result += `;
      }`;
    }

    if (this.props.style?.backgroundColor?.length > 0 || this.props.style?.color?.length > 0) {
      result += `
          .${className.trim()} .ant-picker-clear {
            `;
      if (this.props.style?.backgroundColor?.length > 0)
        result += `background: ${this.props.style?.backgroundColor} !important;`;
      if (this.props.style?.color?.length > 0) result += `color: ${this.props.style?.color} !important;`;
      result += `;
      }`;
    }
    return result;
  };

  getClassName = () => {
    let result = "";
    if (this.props.className) {
      result = this.props.className;
    }
    if (!this.state.uniqueKey) {
      return result;
    }
    result = `${result} kdatepicker_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  disabledDate = (current) => {
    const cur = _.cloneDeep(current);
    const { disabledDays } = this.props;

    if (cur && disabledDays) {
      const day = cur.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const value =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (value) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          if (cur && cur < moment(value)) return true;
        } else {
          if (cur.startOf("day") <= value.startOf("day").add(-1, "day")) return true;
        }
      }
    }

    if (this.props.disableAfterDate || this.state.disableAfterDate) {
      const value =
        this.state.disableAfterDate !== undefined
          ? moment(this.state.disableAfterDate)
          : moment(this.props.disableAfterDate);
      if (value) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          if (cur && cur > moment(value)) return true;
        } else {
          if (cur.startOf("day") >= value.startOf("day").add(1, "day")) return true;
        }
      }
    }

    return undefined;
  };

  range = (start: number, end: number) => {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };

  disabledTime = () => {
    const display = this.getDisplayText();

    if (display !== "Date & Time") return undefined;
    const beforeTime =
      this.props.disableBeforeTime || this.state.disableBeforeTime
        ? moment(this.props.disableBeforeTime || this.state.disableBeforeTime)
        : null;
    const afterTime =
      this.props.disableAfterTime || this.state.disableAfterTime
        ? moment(this.props.disableAfterTime || this.state.disableAfterTime)
        : null;

    if (!beforeTime && !afterTime) {
      return undefined;
    }

    const disabledBeforeHours = beforeTime ? beforeTime.hours() : 0;
    const disabledBeforeMinutes = beforeTime ? beforeTime.minutes() : 0;
    const disabledBeforeSeconds = beforeTime ? beforeTime.seconds() : 0;

    const disabledAfterHours = afterTime ? afterTime.hours() : 23;
    const disabledAfterMinutes = afterTime ? afterTime.minutes() : 59;
    const disabledAfterSeconds = afterTime ? afterTime.seconds() : 59;

    return {
      disabledHours: () => this.range(0, 24).filter((h) => h < disabledBeforeHours || h > disabledAfterHours),
      disabledMinutes: (selectedHour) => {
        if (selectedHour === disabledBeforeHours) {
          return this.range(0, 60).filter((m) => m < disabledBeforeMinutes);
        }
        if (selectedHour === disabledAfterHours) {
          return this.range(0, 60).filter((m) => m > disabledAfterMinutes);
        }
        return [];
      },
      disabledSeconds: (selectedHour, selectedMinute) => {
        if (selectedHour === disabledBeforeHours && selectedMinute === disabledBeforeMinutes) {
          return this.range(0, 60).filter((s) => s < disabledBeforeSeconds);
        }
        if (selectedHour === disabledAfterHours && selectedMinute === disabledAfterMinutes) {
          return this.range(0, 60).filter((s) => s > disabledAfterSeconds);
        }
        return [];
      }
    };

    return undefined;
  };

  setDisableBeforeDate = (value: any) => {
    this.setState({
      disableBeforeDate: value ?? undefined
    });
  };

  // KOA-17521: when disableAfterDate is bound to another Date's value, the update arrives through
  // ReactSystemFunctions.setValueOf with multipleValuePropName === "disableafterdate". Without this
  // imperative setter (the sibling of setDisableBeforeDate), that dispatch fell through to
  // form.setFieldsValue and overwrote this Date's own value instead of only tightening the constraint.
  setDisableAfterDate = (value: any) => {
    this.setState({
      disableAfterDate: value ?? undefined
    });
  };

  setDisableAfterTime = (value: any) => {
    this.setState({
      disableAfterTime: value ?? undefined
    });
  };

  setDisableBeforeTime = (value: any) => {
    this.setState({
      disableBeforeTime: value ?? undefined
    });
  };

  convertToMoment = (value: string | moment.Moment | Date) => {
    if (!value) {
      return null;
    }

    const format = this.props.isTableSearch ? this.props.format : this.getFormat();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const isTime = this.getCalculatedDisplay() === Displays.Time;

    const useFormat = this.props.format === "hh:mm:ss" && this.props.use12Hours === false ? "HH:mm:ss" : format;

    if (moment.isMoment(value)) {
      return value.isValid() ? value.clone() : null;
    }

    if (value instanceof Date) {
      const m = moment(value);
      return m.isValid() ? m : null;
    }

    if (typeof value !== "string") {
      return null;
    }

    const strVal = value;

    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(strVal);

    const jsDateStringPattern = /^[A-Za-z]{3}\s[A-Za-z]{3}\s\d{1,2}\s\d{4}\s\d{2}:\d{2}:\d{2}\sGMT[+-]\d{4}$/;
    if (jsDateStringPattern.test(strVal)) {
      const parsedGmt = moment(strVal, "ddd MMM D YYYY HH:mm:ss [GMT]ZZ", true);
      if (parsedGmt.isValid()) {
        return parsedGmt;
      }
    }

    if (strVal.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/)) {
      return moment(strVal);
    }

    if (isTime) {
      if (/^\d{2}:\d{2}$/.test(strVal) || /^\d{2}:\d{2}:\d{2}$/.test(strVal)) {
        const timeStr = /^\d{2}:\d{2}$/.test(strVal) ? `${strVal}:00` : strVal;
        const existingValue = this.props.value;
        let dateBase: string | undefined;
        if (existingValue && existingValue !== strVal) {
          const existing = moment.isMoment(existingValue) ? existingValue : moment(String(existingValue));
          if (existing.isValid()) {
            dateBase = existing.format("YYYY-MM-DD");
          }
        }
        if (!dateBase) {
          dateBase = moment().format("YYYY-MM-DD");
        }
        return moment(`${dateBase}T${timeStr}`, "YYYY-MM-DDTHH:mm:ss");
      }
    }

    const formatsToTry = [
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm",
      "DD-MM-YYYY",
      "DD.MM.YYYY HH:mm:ss",
      "D.MM.YYYY HH:mm:ss",
      "DD.M.YYYY HH:mm:ss",
      "D.M.YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
      "M/DD/YYYY HH:mm:ss",
      "DD/M/YYYY HH:mm:ss",
      "DD.MM.YYYY HH:mm",
      "D.MM.YYYY HH:mm",
      "MM/DD/YYYY HH:mm",
      "DD/MM/YYYY HH:mm",
      "DD.MM.YYYY",
      "D.MM.YYYY",
      "MM/DD/YYYY",
      "DD/MM/YYYY"
    ];

    if (isDatetime && !hasTimezone && !this.props.isTableSearch) {
      const isoDate = moment.utc(strVal).format();
      const parsedDate = moment(isoDate);
      if (parsedDate.isValid()) {
        return parsedDate;
      }
    }

    if (!this.props.isTableSearch) {
      for (const fmt of formatsToTry) {
        const parsedDate = moment(strVal, fmt, true);
        if (parsedDate.isValid()) {
          if (isDatetime && !hasTimezone) {
            return moment.utc(parsedDate.format(format));
          }
          return moment(parsedDate.format(format));
        }
      }
    }

    const result2 = isDatetime && !hasTimezone ? moment.utc(strVal, useFormat) : moment(strVal, useFormat);
    if (result2.isValid()) return result2;

    const asJsDate = new Date(strVal);
    if (!isNaN(asJsDate.getTime())) {
      const m = moment(asJsDate);
      if (m.isValid()) return m;
    }

    return null;
  };

  convertToString = (value: moment.Moment) => {
    if (value && moment.isMoment(value) && value.isValid()) {
      const format = this.props.isTableSearch ? this.props.format : this.getFormat();
      const isTime = this.getCalculatedDisplay() === Displays.Time;

      return value.format(format);
    }
    return null;
  };

  getFormat = () => {
    if (this.getCalculatedDisplay() === Displays.Date) {
      return "YYYY-MM-DD";
    }
    if (this.getCalculatedDisplay() === Displays.Month) {
      return "YYYY-MM";
    }

    return "YYYY-MM-DDTHH:mm:ssZ";
  };

  getDisplayFormat = () => {
    let format: string = this.props.format || "";

    if (format === "hh:mm:ss" && this.props.use12Hours === false) {
      return "HH:mm:ss";
    }

    if (!this.props.format || this.props.format === "") {
      const mlValue = localStorage.getItem("ml") || "";
      const userLocale = mlValue ? mlValue.split("_")[0] : "";
      const displayType = this.props.displayType;

      // Check for app-configured formats first
      const appDateFormat = ReactSystemFunctions.getAppDateFormat();
      const appTimeFormat = ReactSystemFunctions.getAppTimeFormat();

      if (userLocale) {
        if (displayType === "datetime") {
          if (appDateFormat && appTimeFormat) {
            const momentDateFormat = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
            const momentTimeFormat = appTimeFormat
              .replace(/hh/g, "HH")
              .replace(/mm/g, "mm")
              .replace(/ss/g, "ss")
              .replace(/a/g, "A");
            format = `${momentDateFormat} ${momentTimeFormat}`;
          } else {
            format = `${moment.localeData(userLocale).longDateFormat("L")} ${moment
              .localeData(userLocale)
              .longDateFormat("LT")}`;
          }
        } else if (displayType === "date") {
          if (appDateFormat) {
            format = appDateFormat.replace(/dd/g, "DD").replace(/mm/g, "MM").replace(/yyyy/g, "YYYY");
          } else {
            format = moment.localeData(userLocale).longDateFormat("L");
          }
        } else if (displayType === "time") {
          if (appTimeFormat) {
            format = appTimeFormat.replace(/hh/g, "HH").replace(/mm/g, "mm").replace(/ss/g, "ss").replace(/a/g, "A");
          } else if (this.getCalculatedDisplay() === Displays.Time) {
            if (this.props.format === "mm:ss") {
              format = "mm:ss";
            } else {
              format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
            }
          } else {
            format = this.props.use12Hours ? "hh:mm:ss a" : "HH:mm:ss";
          }
        } else if (displayType === "month") {
          format = moment.localeData(userLocale).longDateFormat("L");
        } else {
          format = "YYYY-MM-DDTHH:mm:ss";
        }
      }
    }

    if (this.props.use12Hours) {
      format = format.replaceAll("HH", "hh");

      if (!format.includes("a") && !format.includes("A")) {
        format += " A";
      }
    } else {
      format = format.replaceAll("hh", "HH");
    }

    return format;
  };

  isDateDisabled = (value: moment.Moment): boolean => {
    if (!value || !value.isValid()) return true;

    const { disabledDays } = this.props;
    if (value && disabledDays) {
      const day = value.day();

      if (
        (disabledDays.includes("Sunday") && day === 0) ||
        (disabledDays.includes("Monday") && day === 1) ||
        (disabledDays.includes("Tuesday") && day === 2) ||
        (disabledDays.includes("Wednesday") && day === 3) ||
        (disabledDays.includes("Thursday") && day === 4) ||
        (disabledDays.includes("Friday") && day === 5) ||
        (disabledDays.includes("Saturday") && day === 6)
      ) {
        return true;
      }

      if (disabledDays.includes("Weekends") && (day === 0 || day === 6)) {
        return true;
      }

      if (disabledDays.includes("Weekdays") && day >= 1 && day <= 5) {
        return true;
      }
    }

    if (this.props.disableBeforeDate || this.state.disableBeforeDate) {
      const disableValue =
        this.state.disableBeforeDate !== undefined
          ? moment(this.state.disableBeforeDate)
          : moment(this.props.disableBeforeDate);
      if (disableValue) {
        if (this.getCalculatedDisplay() != Displays.Date) {
          return value < disableValue;
        }
        return value.startOf("day") <= disableValue.startOf("day").add(-1, "day");
      }
    }
    return false;
  };

  handleChange = (value: any) => {
    if (value === null || value === undefined || value === "") {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
      return;
    }

    try {
      const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
      const isTime = this.getCalculatedDisplay() === Displays.Time;
      let momentValue;

      if (moment.isMoment(value)) {
        if (isTime) {
          const existingMoment = this.convertToMoment(this.props.value as string | moment.Moment | Date);
          if (existingMoment && existingMoment.isValid()) {
            momentValue = existingMoment
              .clone()
              .hour(value.hour())
              .minute(value.minute())
              .second(value.second())
              .millisecond(value.millisecond());
          } else {
            momentValue = value.clone();
          }
        } else if (isDatetime) {
          momentValue = moment(value).local();
        } else {
          momentValue = value;
        }
        if (this.props.isTableSearch) {
          momentValue = value.clone();
        }
      } else {
        momentValue = this.convertToMoment(value);
      }

      if (!momentValue || !momentValue.isValid()) {
        if (this.props.onChange) {
          this.props.onChange(null);
        }
        return;
      }

      if (this.isDateDisabled(momentValue)) {
        return;
      }

      if (isDatetime) {
        momentValue = this.findFirstAvailableTime(momentValue);
      }

      const adjustedMomentValue = this.validateTimeSteps(momentValue);
      const dateValue = this.convertToString(adjustedMomentValue);

      if (this.props.onChange) {
        if (this.props.returnDisplayValue) {
          const displayValue = adjustedMomentValue.format(this.getDisplayFormat());
          this.props.onChange(displayValue);
        } else {
          this.props.onChange(dateValue);
        }
      }
    } catch (error) {
      if (this.props.onChange) {
        this.props.onChange(null);
      }
    }
  };

  getGenericValue = () => {
    const val = this.props.value as unknown;
    if (val === undefined || val === null || val === "") {
      return {};
    }
    const m = this.convertToMoment(val as any);
    if (m) {
      return { value: m };
    }
    return {};
  };

  getGenericDefaultValue = () => {
    let result: any = {};
    const def = this.props.defaultValue as unknown;
    if (def !== undefined && def !== null) {
      if (typeof def === "string") {
        if (def.trim() === "") {
          return { defaultValue: null };
        }
        result = { defaultValue: this.convertToMoment(def.trim()) };
      } else if (moment.isMoment(def) || def instanceof Date) {
        result = {
          defaultValue: this.convertToMoment(def as moment.Moment | Date)
        };
      }
    }
    return result;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    let style: any = _.clone(this.props.style);
    if (props.defaultValue) {
      delete props.defaultValue;
    }
    if (props.disabledDate) {
      delete props.disabledDate;
    }
    if (props.value) {
      delete props.value;
    }
    if (_.isString(props.value) && props.value.length === 0) {
      delete props.value;
    }
    if (!props.placeholder || props.placeholder.length === 0) {
      props.placeholder = "";
    }
    if (!style) {
      style = {
        width: "100%"
      };
    } else if (!style.width) {
      style.width = "100%";
    }
    if (style.display == "block") {
      style.display = "flex";
    } else if (style.display == "inline") {
      style.display = "inline-flex";
    }
    props.style = style;
    if (this.getCalculatedDisplay() === Displays.DateTime) {
      props.showHour = true;
      props.showMinute = true;
      props.showSecond = true;
    }

    if (this.props.hourStep !== undefined && (this.props.hourStep <= 0 || this.props.hourStep > 24)) {
      delete props.hourStep;
    }

    if (this.props.minuteStep !== undefined && (this.props.minuteStep <= 0 || this.props.minuteStep > 60)) {
      delete props.minuteStep;
    }

    if (this.props.secondStep !== undefined && (this.props.secondStep <= 0 || this.props.secondStep > 60)) {
      delete props.secondStep;
    }

    if (props.format === "hh:mm:ss" && props.use12Hours === false) {
      props.format = "HH:mm:ss";
    }

    return props;
  };

  getDisplayText = () => {
    const display = this.getCalculatedDisplay();
    let displayText = "Date";
    switch (display) {
      case Displays.Date:
        displayText = "Date";
        break;
      case Displays.DateTime:
        displayText = "Date & Time";
        break;
      case Displays.Time:
        displayText = "Time";
        break;
      case Displays.Month:
        displayText = "Month";
        break;
      default:
        displayText = "Date";
        break;
    }
    return displayText;
  };

  getDefaultTimeValue = (): moment.Moment => {
    const defaultTime = moment("00:00:00", "HH:mm:ss");

    if (this.props.disableBeforeTime) {
      const beforeTime = moment(this.props.disableBeforeTime);
      if (beforeTime.isValid()) {
        const beforeHour = beforeTime.hour();
        const beforeMinute = beforeTime.minute();
        const beforeSecond = beforeTime.second();

        if (beforeHour > 0 || beforeMinute > 0 || beforeSecond > 0) {
          return beforeTime.clone();
        }
      }
    }

    return defaultTime;
  };

  findFirstAvailableTime = (momentValue: moment.Moment): moment.Moment => {
    if (
      (!this.props.disableBeforeTime && !this.props.disableAfterTime) ||
      this.getCalculatedDisplay() !== Displays.DateTime
    ) {
      return momentValue;
    }

    const testValue = momentValue.clone();
    const currentHour = testValue.hour();
    const currentMinute = testValue.minute();
    const currentSecond = testValue.second();

    if (this.props.disableBeforeTime) {
      const beforeTime = moment(this.props.disableBeforeTime);
      if (beforeTime.isValid()) {
        const beforeHour = beforeTime.hour();
        const beforeMinute = beforeTime.minute();
        const beforeSecond = beforeTime.second();

        if (
          currentHour < beforeHour ||
          (currentHour === beforeHour && currentMinute < beforeMinute) ||
          (currentHour === beforeHour && currentMinute === beforeMinute && currentSecond < beforeSecond)
        ) {
          if (this.props.disableAfterTime) {
            const afterTime = moment(this.props.disableAfterTime);
            if (afterTime.isValid()) {
              const afterHour = afterTime.hour();
              const afterMinute = afterTime.minute();
              const afterSecond = afterTime.second();

              if (
                beforeHour > afterHour ||
                (beforeHour === afterHour && beforeMinute > afterMinute) ||
                (beforeHour === afterHour && beforeMinute === afterMinute && beforeSecond > afterSecond)
              ) {
                return testValue.hour(afterHour).minute(afterMinute).second(afterSecond).add(1, "second");
              }
            }
          }

          return testValue.hour(beforeHour).minute(beforeMinute).second(beforeSecond);
        }
      }
    }

    if (this.props.disableAfterTime) {
      const afterTime = moment(this.props.disableAfterTime);
      if (afterTime.isValid()) {
        const afterHour = afterTime.hour();
        const afterMinute = afterTime.minute();
        const afterSecond = afterTime.second();

        if (
          currentHour > afterHour ||
          (currentHour === afterHour && currentMinute > afterMinute) ||
          (currentHour === afterHour && currentMinute === afterMinute && currentSecond > afterSecond)
        ) {
          return testValue.hour(afterHour).minute(afterMinute).second(afterSecond);
        }
      }
    }

    return momentValue;
  };

  validateTimeSteps = (momentValue: moment.Moment): moment.Moment => {
    if (
      !momentValue ||
      !momentValue.isValid() ||
      !(this.props.hourStep || this.props.minuteStep || this.props.secondStep)
    ) {
      return momentValue;
    }

    const display = this.getCalculatedDisplay();
    if (display !== Displays.DateTime && display !== Displays.Time) {
      return momentValue;
    }

    const isTime = display === Displays.Time;

    let adjustedValue = momentValue.clone();

    if (!isTime) {
      const originalOffset = momentValue.utcOffset();
      adjustedValue = adjustedValue.utcOffset(originalOffset);
    }

    if (this.props.hourStep && this.props.hourStep > 0 && this.props.hourStep <= 24) {
      const currentHour = adjustedValue.hour();
      const adjustedHour = Math.round(currentHour / this.props.hourStep) * this.props.hourStep;
      adjustedValue = adjustedValue.hour(Math.min(adjustedHour, 23));
    }

    if (this.props.minuteStep && this.props.minuteStep > 0 && this.props.minuteStep <= 60) {
      const currentMinute = adjustedValue.minute();
      const adjustedMinute = Math.round(currentMinute / this.props.minuteStep) * this.props.minuteStep;
      adjustedValue = adjustedValue.minute(Math.min(adjustedMinute, 59));
    }

    if (this.props.secondStep && this.props.secondStep > 0 && this.props.secondStep <= 60) {
      const currentSecond = adjustedValue.second();
      const adjustedSecond = Math.round(currentSecond / this.props.secondStep) * this.props.secondStep;
      adjustedValue = adjustedValue.second(Math.min(adjustedSecond, 59));
    }

    return adjustedValue;
  };

  parseMultipleValue = (): moment.Moment[] => {
    const val = this.props.value;
    if (!val) return [];

    // Array of string | Moment | Date
    if (Array.isArray(val)) {
      return val.map((item) => this.convertToMoment(item as any)).filter((m): m is moment.Moment => !!m && m.isValid());
    }

    // Single Moment or Date — wrap as single-item array
    if (moment.isMoment(val) && val.isValid()) return [val.clone()];
    if (val instanceof Date) {
      const m = moment(val);
      return m.isValid() ? [m] : [];
    }

    if (typeof val !== "string" || val.trim() === "") return [];

    // Comma-separated string: "2026-04-01,2026-04-05"
    return val
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean)
      .map((d) => this.convertToMoment(d))
      .filter((m): m is moment.Moment => !!m && m.isValid());
  };

  getMultipleCompFormat = (): string => {
    return this.getCalculatedDisplay() === Displays.Month ? "YYYY-MM" : "YYYY-MM-DD";
  };

  handleMultipleAdd = (value: any) => {
    if (!value) return;
    const date = this.convertToMoment(value);
    if (!date || !date.isValid()) return;

    // Respect the same disabled-date rules as the single picker
    if (this.isDateDisabled(date)) return;

    const compFormat = this.getMultipleCompFormat();
    const currentDates = this.parseMultipleValue();
    const alreadySelected = currentDates.some((d) => d.format(compFormat) === date.format(compFormat));

    // Toggle: clicking an already-selected date removes it
    const newDates = alreadySelected
      ? currentDates.filter((d) => d.format(compFormat) !== date.format(compFormat))
      : [...currentDates, date];

    // A calendar pick just happened: keep the panel open across it so the user can go on
    // selecting more dates. antd will fire onOpenChange(false) right after this onChange
    // (its default single-date auto-close); handleMultipleOpenChange consumes that flag.
    this.keepMultiplePanelOpen = true;
    this.emitMultipleChange(newDates);
  };

  handleMultipleRemove = (index: number) => {
    const newDates = this.parseMultipleValue().filter((_, i) => i !== index);
    this.emitMultipleChange(newDates);
  };

  emitMultipleChange = (dates: moment.Moment[]) => {
    if (!this.props.onChange) return;
    if (dates.length === 0) {
      this.props.onChange(null);
      return;
    }
    const format = this.getFormat();
    this.props.onChange(dates.map((d) => d.format(format)).join(","));
  };

  renderMultiplePicker = () => {
    const display = this.getDisplayText();

    // Multiple mode only applies to Date and Month pickers.
    // Fall back to single picker render for Time / DateTime.
    if (display === "Time" || display === "Date & Time") {
      return this.renderSinglePicker();
    }

    const selectedDates = this.parseMultipleValue();
    const locale = this.getLocale();
    const displayFormat = this.getDisplayFormat();
    const { style, placeholder, disabled } = this.props;

    // Spread user styles first, then enforce layout-only overrides.
    // Border, border-radius and focus ring are provided by the ant-picker CSS class.
    const containerStyle: any = {
      width: "100%",
      ...style,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "4px",
      height: undefined,
      minHeight: "32px",
      cursor: disabled ? "not-allowed" : "text",
      opacity: disabled ? 0.4 : style?.opacity
    };

    const containerClass = [
      "ant-picker",
      this.state.isOpen ? "ant-picker-focused" : "",
      disabled ? "ant-picker-disabled" : "",
      this.getClassName()
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div style={containerStyle} className={containerClass}>
        {selectedDates.map((date, index) => (
          <Tag
            key={`${date.valueOf()}-${index}`}
            closable={!disabled}
            onClose={(e) => {
              e.preventDefault();
              this.handleMultipleRemove(index);
            }}
            style={{ marginRight: 0, userSelect: "none" }}
          >
            {date.format(displayFormat)}
          </Tag>
        ))}
        <DatePicker
          open={this.state.isOpen}
          onOpenChange={this.handleMultipleOpenChange}
          disabledDate={this.disabledDate}
          locale={locale}
          disabled={disabled}
          value={null}
          placeholder={selectedDates.length === 0 ? placeholder || "" : ""}
          format={displayFormat}
          picker={display === "Month" ? "month" : "date"}
          onChange={this.handleMultipleAdd}
          bordered={false}
          allowClear={false}
          style={{
            border: "none",
            boxShadow: "none",
            background: "transparent",
            padding: 0,
            flex: 1,
            minWidth: "80px"
          }}
        />
      </div>
    );
  };

  handleKeydown = (e: any, rcPreventDefault?: () => void) => {
    const charCode = e.which ? e.which : e.keyCode;

    // In-panel keyboard navigation (KOA-14173). While the panel is open, Tab / Shift+Tab
    // walk a visible highlight through the date grid, the time columns and the OK button;
    // arrow keys change the highlighted date/time; Enter confirms. Real DOM focus stays on
    // the input, so rc-picker keeps routing keydowns here and focus leaves naturally at the
    // panel boundary. rcPreventDefault (rc-picker's own preventDefault callback) suppresses
    // rc-picker's default Tab handling, which would otherwise cancel the event and trap focus.
    if (this.state.isOpen) {
      const stopCount: number = getPanelStopCount();
      if (stopCount > 0) {
        if (charCode === 9) {
          const forward: boolean = !e.shiftKey;
          if (forward && this.panelFocusIndex < stopCount - 1) {
            this.panelFocusIndex += 1;
            highlightPanelStop(this.panelFocusIndex);
            e.preventDefault();
            rcPreventDefault?.();
            return;
          }
          if (!forward && this.panelFocusIndex > -1) {
            this.panelFocusIndex -= 1;
            highlightPanelStop(this.panelFocusIndex);
            e.preventDefault();
            rcPreventDefault?.();
            return;
          }
          // Reached a boundary — clear the highlight and fall through to leave the picker.
          this.panelFocusIndex = -1;
          clearPanelHighlight();
        } else if (this.panelFocusIndex >= 0 && ARROW_KEYS[charCode]) {
          const moved: boolean = navigatePanelStop(this.panelFocusIndex, ARROW_KEYS[charCode]);
          if (moved) {
            e.preventDefault();
            rcPreventDefault?.();
            return;
          }
        } else if (this.panelFocusIndex >= 0 && charCode === 13) {
          // Enter confirms the open panel via its OK button. Consume it regardless so it
          // never falls through to the separator-insertion logic below and mangles the input.
          confirmOpenPanel();
          e.preventDefault();
          rcPreventDefault?.();
          return;
        }
      }
    }

    // Tab at the panel boundary (or on a closed picker): commit any already-typed value,
    // then let focus leave to the next/previous field. Never cancel the Tab ourselves;
    // rcPreventDefault stops rc-picker from cancelling it (KOA-14173).
    if (charCode === 9) {
      if (this.state.isOpen) {
        const val = moment(e?.target?.value, this.props.format ?? "");
        if (val.isValid() && !this.isDateDisabled(val)) {
          const adjustedVal = this.validateTimeSteps(val);
          this.handleChange(moment(adjustedVal));
        }
      }
      rcPreventDefault?.();
      if (this.props.onKeyDown) {
        this.props.onKeyDown(e, () => e.preventDefault());
      }
      return;
    }
    const { format } = this.props;
    if (
      format !== "YYYY-MM-DD HH:mm:ss" &&
      format !== "DD-MM-YYYY HH:mm:ss" &&
      format !== "YYYY-MM-DD HH:mm" &&
      format !== "DD-MM-YYYY HH:mm" &&
      e.target.value.length === 10 &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (
      charCode > 31 &&
      (charCode > 57 || charCode < 48) &&
      (charCode > 105 || charCode < 96) &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (format === "YYYY/MM/DD" || format === "YYYY/DD/MM") {
      this.addSlashToDateInput(e, 4, 7, charCode);
    } else if (format === "YYYY-MM-DD HH:mm:ss" || format === "YYYY-MM-DD HH:mm") {
      this.addHyphenToDateInput(e, 4, 7, charCode);
    } else if (format === "DD/MM/YYYY" || format === "MM/DD/YYYY") {
      this.addSlashToDateInput(e, 2, 5, charCode);
    } else if (format === "DD-MM-YYYY HH:mm:ss" || format === "DD-MM-YYYY HH:mm") {
      this.addHyphenToDateInput(e, 2, 5, charCode);
    } else if (format === "DD.MM.YYYY" || format === "MM.DD.YYYY") {
      this.addDotToDateInput(e, 2, 5, charCode);
    } else if (format === "YYYY.MM.DD" || format === "YYYY.DD.MM") {
      this.addDotToDateInput(e, 4, 7, charCode);
    } else if (format === "YYYY-MM-DD") {
      if (e.target.value.length === 10) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addHyphenToDateInput(e, 4, 7, charCode);
    } else if (format === "DD-MM-YYYY") {
      if (e.target.value.length === 10) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addHyphenToDateInput(e, 2, 5, charCode);
    } else if (format === "MM/DD") {
      if (e.target.value.length === 5) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 2, undefined, charCode);
    } else if (format === "MM.DD") {
      if (e.target.value.length === 5) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addDotToDateInput(e, 2, undefined, charCode);
    } else if (format === "YYYY/MM") {
      if (e.target.value.length === 7) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addSlashToDateInput(e, 4, undefined, charCode);
    } else if (format === "YYYY.MM") {
      if (e.target.value.length === 7) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      this.addDotToDateInput(e, 4, undefined, charCode);
    } else if (format === "D M Y") {
      e.target.value = e.target.value;
    } else if (format === undefined) {
      this.addSlashToDateInput(e, 4, 7, charCode);
    }
  };

  addSlashToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (e.target.value.length === firstLength && charCode !== 8 && charCode !== 46 && charCode !== 186) {
      e.target.value = `${e.target.value}/`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.target.value = `${e.target.value}/`;
    } else e.target.value = e.target.value.replace(/[^0-9/]/g, "");
  };

  addDotToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (
      e.target.value.length === firstLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.target.value = `${e.target.value}.`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186 &&
      charCode !== 190 &&
      charCode !== 110
    ) {
      e.target.value = `${e.target.value}.`;
    } else e.target.value = e.target.value.replace(/[^0-9.]/g, "");
  };

  addHyphenToDateInput = (e: any, firstLength: number, secondLength: number | undefined, charCode: number) => {
    if (e.target.value.length === firstLength && charCode !== 8 && charCode !== 46 && charCode !== 186) {
      e.target.value = `${e.target.value}-`;
    } else if (
      secondLength &&
      e.target.value.length === secondLength &&
      charCode !== 8 &&
      charCode !== 46 &&
      charCode !== 186
    ) {
      e.target.value = `${e.target.value}-`;
    } else e.target.value = e.target.value.replace(/[^0-9-]/g, "");
  };

  handleOpenChange = (isOpen: boolean) => {
    if (window?.kuika?.dashboardState !== DashboardState.design) {
      // Reset the in-panel highlight whenever the panel opens or closes (KOA-14173).
      this.panelFocusIndex = -1;
      this.setState({ isOpen });
    }
  };

  /**
   * Open-state handler for multiple mode (KOA-17737).
   *
   * In multiple mode the picker is a single-date antd DatePicker reused to collect many dates.
   * antd auto-closes a single-date panel after every pick, which forced the user to reopen the
   * calendar for each date. Because `open` is fully controlled here, we can simply ignore the
   * selection-driven close: `handleMultipleAdd` sets `keepMultiplePanelOpen` just before the
   * pick commits, and the very next onOpenChange(false) is swallowed so the panel stays open.
   * A real dismissal (outside click / Esc) arrives with no preceding selection, so the flag is
   * false and the panel closes normally — committing every date the user chose.
   */
  handleMultipleOpenChange = (isOpen: boolean) => {
    if (window?.kuika?.dashboardState === DashboardState.design) {
      return;
    }
    if (!isOpen && this.keepMultiplePanelOpen) {
      this.keepMultiplePanelOpen = false;
      // Leave state untouched: the panel is already open and stays open, no re-render needed.
      return;
    }
    this.keepMultiplePanelOpen = false;
    this.panelFocusIndex = -1;
    this.setState({ isOpen });
  };

  handleBlur = (e: any) => {
    const inputValue = e?.target?.value;
    if (inputValue) {
      const val = moment(inputValue, this.getDisplayFormat());
      if (val.isValid() && !this.isDateDisabled(val)) {
        const adjustedVal = this.validateTimeSteps(val);
        this.handleChange(adjustedVal);
      }
    }
  };

  getLocale = () => {
    const locale = ReactSystemFunctions.getLocale();
    // rc-picker resolves the panel's first weekday from the antd locale's `locale` string, falling
    // back to the active moment locale and ultimately "en"; normalise all three to a Monday start.
    const localeName: string = (locale as AntdLocaleShape)?.locale || moment.locale();
    forceMondayFirstDayOfWeek(localeName);
    forceMondayFirstDayOfWeek(moment.locale());
    forceMondayFirstDayOfWeek("en");
    return locale;
  };

  renderSinglePicker = () => {
    const display = this.getDisplayText();
    const isDatetime = this.getCalculatedDisplay() === Displays.DateTime;
    const locale = this.getLocale();

    let timeFormat = this.props.format;
    if (timeFormat === "hh:mm:ss" && this.props.use12Hours === false) {
      timeFormat = "HH:mm:ss";
    }

    return (
      <>
        {(display === "Date" || display === "Date & Time" || display === "Month") && (
          <DatePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            disabledDate={this.disabledDate}
            disabledTime={this.disabledTime}
            locale={locale}
            {...this.getProps()}
            showTime={
              display === "Date & Time" || this.props.displayType === "datetime"
                ? {
                    defaultValue: this.getDefaultTimeValue(),
                    utcOffset: isDatetime ? moment().utcOffset() : undefined
                  }
                : false
            }
            picker={display === "Month" ? "month" : "date"}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onKeyDown={(e, preventDefault) => this.handleKeydown(e, preventDefault)}
            format={this.getDisplayFormat()}
          ></DatePicker>
        )}
        {display === "Time" && (
          <TimePicker
            open={this.state.isOpen}
            onOpenChange={this.handleOpenChange}
            className={this.getClassName()}
            locale={locale}
            {...this.getProps()}
            {...this.getGenericValue()}
            {...this.getGenericDefaultValue()}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
            format={this.getDisplayFormat()}
          ></TimePicker>
        )}
      </>
    );
  };

  render(): ReactNode {
    if (this.props.multiple) {
      return this.renderMultiplePicker();
    }
    return this.renderSinglePicker();
  }
}
const kDatePicker = withCommonEvents(KDatePicker);
export { kDatePicker as KDatePicker };
